import {Mapper} from '@s-ui/domain'

import {DealerProfileRatingsValueObject} from '../../ratings/ValueObjects/DealerProfileRatingsValueObject.js'

export class FromDealerRawDataToDealerEntityMapper extends Mapper {
  /**
   * @constructor
   * @param {Object} param
   * @param {Object} param.config
   * @param {Function} param.dealerEntityFactory
   * @param {FromDealerLocationJsonToLocationValueObjectMapper} param.fromDealerLocationJsonToLocationValueObjectMapper
   * @param {GetUrlService} param.getUrlService
   */
  constructor({
    config,
    dealerEntityFactory,
    dealerServiceValueObjectFactory,
    fromDealerLocationJsonToLocationValueObjectMapper,
    getUrlService
  }) {
    super()

    this._config = config
    this._dealerEntityFactory = dealerEntityFactory
    this._dealerServiceValueObjectFactory = dealerServiceValueObjectFactory
    this._fromDealerLocationJsonToLocationValueObjectMapper = fromDealerLocationJsonToLocationValueObjectMapper
    this._getUrlService = getUrlService
  }

  _getStockPageUrl = ({dealerSlug, pack}) => {
    if (!dealerSlug) return

    const {DEALER_PACKS} = this._config.get('dealer')
    const hasSubscriptionDealerPack = pack === DEALER_PACKS.SUBSCRIPTION

    return hasSubscriptionDealerPack
      ? this._getUrlService.execute({
          pageName: 'PROFESSIONAL_STOCK_RENTING',
          params: {dealerSlug}
        })
      : this._getUrlService.execute({
          pageName: 'PROFESSIONAL_STOCK',
          params: {dealerSlug}
        })
  }

  _mapDealerStore = async store => {
    const {
      dealerIdentity: {id},
      dealerName: name,
      location,
      stockPageUrl: dealerSlug,
      totalResults: totalPublishedVehicles
    } = store

    const stockPageUrlVO = await this._getStockPageUrl({
      dealerSlug
    })

    const stockPageUrl = stockPageUrlVO && stockPageUrlVO.getUrl()

    const locationVO = this._fromDealerLocationJsonToLocationValueObjectMapper.map(location)

    return this._dealerEntityFactory({
      id,
      location: locationVO,
      name,
      stockPageUrl,
      totalPublishedVehicles
    })
  }

  /**
   * @param {Object} rawData
   * @returns {DealerEntity}
   */
  map = async rawData => {
    const {
      brandingActive,
      showContactLogo,
      showDealerDirections,
      contact: {phone: phoneNumber, hasCallTrackingService, hasWhatsappProduct, hidePhone},
      externalContractId, // companyWasId
      externalPageUrl,
      hasFinancingProduct,
      hasReservation,
      id,
      location,
      logoUrl: logoImageUrl,
      moreInformation,
      name,
      pack: {type: pack} = {},
      ratings,
      relatedStores: stores = [],
      schedule,
      services = [],
      mainRentingServicesIncluded,
      stockPageUrl: dealerSlug,
      isActive,
      storeImageUrls,
      videos
    } = rawData

    const hasRatings = Boolean(ratings)

    const servicesVoList = services.map(({type, title, description, included}) => {
      return this._dealerServiceValueObjectFactory({type, title, description, isIncluded: included})
    })
    const locationVO = this._fromDealerLocationJsonToLocationValueObjectMapper.map(location)

    const stockPageUrlVO = await this._getStockPageUrl({
      dealerSlug,
      pack
    })

    const stockPageUrl = stockPageUrlVO && stockPageUrlVO.getUrl()
    const hasHidePhone = Boolean(hidePhone !== undefined)

    const relatedStores = await Promise.all(stores.map(this._mapDealerStore))

    const ratingsValueObject = DealerProfileRatingsValueObject.createFromApiResponse({
      badges: ratings?.badges,
      commentsNumber: ratings?.commentsNumber,
      scoreAverage: ratings?.scoreAverage
    })

    return this._dealerEntityFactory({
      brandingActive,
      showContactLogo,
      showDealerDirections,
      externalContractId,
      externalPageUrl,
      hasCallTrackingService,
      hasFinancingProduct,
      hasReservation,
      hasWhatsappProduct,
      id,
      location: locationVO,
      logoImageUrl,
      sellerMessagingId: `pro-${id}`,
      moreInformation,
      name,
      pack,
      phoneNumber,
      relatedStores,
      schedule,
      stockPageUrl,
      storeImageUrls,
      videos,
      isActive,
      ...(hasHidePhone && {hidePhone}),
      ...(hasRatings && {ratings: ratingsValueObject.value}),
      ...(services.length > 0 && {services: servicesVoList, mainRentingServicesIncluded})
    })
  }
}
